import { FormikErrors, useFormikContext } from 'formik'
import { useEffect } from 'react'

interface ScrollIntoViewOptionsExtended extends ScrollIntoViewOptions {
  behavior?: ScrollBehavior
  block?: ScrollLogicalPosition
  inline?: ScrollLogicalPosition
}

export const ScrollToFieldError = ({
  scrollBehavior = {
    behavior: 'smooth',
    block: 'center',
  } as ScrollIntoViewOptionsExtended,
  customErrorFields = [],
}: {
  scrollBehavior?: ScrollIntoViewOptionsExtended
  customErrorFields?: string[]
}) => {
  const { submitCount, isValid, errors } = useFormikContext()

  useEffect(() => {
    if (isValid) return

    const fieldErrorNames = getFieldErrorNames(errors, customErrorFields)

    if (fieldErrorNames.length <= 0) return

    const renderedElements = document.querySelectorAll<
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement
      | HTMLButtonElement
      | HTMLDivElement
    >('input, textarea, select, button, div')

    const findFirstDisplayedField = () => {
      for (const rendered of renderedElements) {
        let name = (rendered as HTMLInputElement)?.name || rendered?.id
        if (name?.includes('file-input')) {
          name = name.replace('file-input-', '')
        }
        if (fieldErrorNames?.includes(name)) {
          return name
        }
      }
      return null
    }
    const firstDisplayedField = findFirstDisplayedField()

    const element =
      document.querySelector<HTMLInputElement>(
        `[name='${firstDisplayedField}']`
      ) ||
      document.querySelector<HTMLInputElement>(`[id='${firstDisplayedField}']`)

    if (!element) return
    element.scrollIntoView(scrollBehavior)
  }, [submitCount])

  return null
}

export const getFieldErrorNames = (
  formikErrors: FormikErrors<any>,
  customErrorFields: string[]
) => {
  const transformObjectToDotNotation = (
    obj: { [key: string]: any },
    prefix = '',
    result: string[] = []
  ): string[] => {
    Object.keys(obj).forEach(key => {
      const value = obj[key]
      if (!value) return

      const nextKey: string = prefix ? `${prefix}.${key}` : key // Ensure nextKey is typed as a string

      if (typeof value === 'object' && !Array.isArray(value)) {
        transformObjectToDotNotation(value, nextKey, result)
      } else if (typeof value === 'object' && Array.isArray(value)) {
        transformObjectToDotNotation(value, nextKey, result)
      } else {
        result.push(nextKey)
      }
    })

    return result
  }

  return transformObjectToDotNotation(formikErrors).concat(customErrorFields)
}
